import type { Language } from '@lexicon/core';

export type ApiErrorKind = 'network' | 'server' | 'not-found' | 'rate-limited';

export type ApiResult<T> =
  | { ok: true; value: T }
  | { ok: false; error: ApiErrorKind };

export interface CreateChallengeBody {
  word: string;
  challengerName?: string;
  deviceId: string;
  /**
   * The word list the word came from. Optional on the wire so an older
   * client still works, in which case the worker identifies the language
   * from the word itself.
   */
  lang?: Language;
}

export interface ChallengeInfo {
  /** Obfuscated word (spec §4.3) — decode with core's `deobfuscate`. */
  word: string;
  challengerName?: string;
  /**
   * The challenge's language. Absent only from a worker older than this
   * field; callers fall back to identifying it from the word.
   */
  lang?: Language;
}

export interface ChallengeResultBody {
  deviceId: string;
  playerName?: string;
  won: boolean;
  guessCount: number;
}

export interface ChallengeResult {
  playerName?: string;
  won: boolean;
  guessCount: number;
  at?: number;
}

const DEFAULT_API_BASE = 'https://lexicon-api.placeholder.workers.dev';

/** Worker origin. Override at build time with `VITE_API_BASE`. */
export const API_BASE: string =
  (import.meta.env.VITE_API_BASE ?? '').replace(/\/+$/, '') || DEFAULT_API_BASE;

async function request<T>(path: string, init?: RequestInit): Promise<ApiResult<T>> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      ...init,
      headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
    });
  } catch {
    return { ok: false, error: 'network' };
  }
  if (res.status === 404) return { ok: false, error: 'not-found' };
  if (res.status === 429) return { ok: false, error: 'rate-limited' };
  if (!res.ok) return { ok: false, error: 'server' };
  if (res.status === 204) return { ok: true, value: undefined as T };
  try {
    return { ok: true, value: (await res.json()) as T };
  } catch {
    return { ok: false, error: 'server' };
  }
}

/** `POST /challenge` → `{code}` (spec §4.3). */
export async function createChallenge(body: CreateChallengeBody): Promise<ApiResult<string>> {
  const res = await request<{ code?: unknown }>('/challenge', {
    method: 'POST',
    body: JSON.stringify(body),
  });
  if (!res.ok) return res;
  const code = res.value?.code;
  if (typeof code !== 'string' || code.length === 0) return { ok: false, error: 'server' };
  return { ok: true, value: code };
}

/** `GET /challenge/:code` → obfuscated word + optional challenger name. */
export async function getChallenge(code: string): Promise<ApiResult<ChallengeInfo>> {
  const res = await request<{ word?: unknown; challengerName?: unknown }>(
    `/challenge/${encodeURIComponent(code)}`,
  );
  if (!res.ok) return res;
  const word = res.value?.word;
  if (typeof word !== 'string' || word.length === 0) return { ok: false, error: 'server' };
  const name = res.value?.challengerName;
  return {
    ok: true,
    value: typeof name === 'string' && name.length > 0 ? { word, challengerName: name } : { word },
  };
}

/**
 * `POST /challenge/:code/result`. Non-critical (spec §7): one silent retry
 * on network/server failure, then the result is dropped. Never throws.
 * Returns whether the result reached the server.
 */
export async function postChallengeResult(
  code: string,
  body: ChallengeResultBody,
): Promise<boolean> {
  for (let attempt = 0; attempt < 2; attempt++) {
    const res = await request<void>(`/challenge/${encodeURIComponent(code)}/result`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
    if (res.ok) return true;
    // A missing challenge or a rate limit will not heal on a retry.
    if (res.error === 'not-found' || res.error === 'rate-limited') return false;
  }
  return false;
}

/**
 * `POST /ping {deviceId}` (spec §11) — one anonymous play ping a day.
 * Fire-and-forget: never throws or rejects, no retry. A dropped ping is an
 * acceptable tradeoff, not an error the caller needs to handle.
 */
export async function pingPlay(deviceId: string): Promise<void> {
  try {
    await fetch(`${API_BASE}/ping`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ deviceId }),
    });
  } catch {
    /* swallow — non-critical, fire-and-forget (spec §11) */
  }
}

function isChallengeResult(v: unknown): v is ChallengeResult {
  if (v === null || typeof v !== 'object') return false;
  const r = v as Record<string, unknown>;
  return typeof r.won === 'boolean' && typeof r.guessCount === 'number';
}

/** `GET /challenge/:code/results?deviceId=` — creator only. */
export async function getChallengeResults(
  code: string,
  deviceId: string,
): Promise<ApiResult<ChallengeResult[]>> {
  const res = await request<{ results?: unknown }>(
    `/challenge/${encodeURIComponent(code)}/results?deviceId=${encodeURIComponent(deviceId)}`,
  );
  if (!res.ok) return res;
  const raw = res.value?.results;
  if (!Array.isArray(raw)) return { ok: false, error: 'server' };
  return { ok: true, value: raw.filter(isChallengeResult) };
}
